import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import CustomDropdown from "../../../../../components/CustomDropdown/CustomDropdown";

const GROUPS_KEY = "novala.customReportGroups";
const REPORT_GROUPS_KEY = "novala.reportGroupAssignments";
const ADD_NEW = "__add_new__";

interface Props {
  open: boolean;
  onClose: () => void;
  accountId: string;
  accountName: string;
}

export default function AddToGroupModal({ open, onClose, accountId, accountName }: Props) {
  const [groupValue, setGroupValue] = useState<string>("");
  const [newGroupName, setNewGroupName] = useState("");
  const [existingGroups, setExistingGroups] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    try {
      const raw = window.localStorage.getItem(GROUPS_KEY);
      setExistingGroups(raw ? JSON.parse(raw) : []);
      setGroupValue("");
      setNewGroupName("");
    } catch {}
  }, [open]);

  if (!open) return null;

  const isAddingNew = groupValue === ADD_NEW;

  const options = [
    ...existingGroups.map((g) => ({ label: g, value: g })),
    { label: "+ Add new group", value: ADD_NEW, divider: true, accent: true },
  ];

  const handleAdd = () => {
    let groupName = "";
    if (isAddingNew) {
      groupName = newGroupName.trim();
      if (!groupName) return;
      if (!existingGroups.includes(groupName)) {
        const updated = [...existingGroups, groupName];
        try { window.localStorage.setItem(GROUPS_KEY, JSON.stringify(updated)); } catch {}
      }
    } else if (groupValue) {
      groupName = groupValue;
    } else {
      return;
    }

    try {
      const raw = window.localStorage.getItem(REPORT_GROUPS_KEY);
      const assignments = raw ? JSON.parse(raw) : {};
      if (!assignments[groupName]) assignments[groupName] = [];
      if (!assignments[groupName].some((a: any) => a.accountId === accountId)) {
        assignments[groupName].push({ accountId, accountName, addedAt: new Date().toISOString() });
      }
      window.localStorage.setItem(REPORT_GROUPS_KEY, JSON.stringify(assignments));
    } catch {}
    onClose();
  };

  const canSave = isAddingNew ? newGroupName.trim().length > 0 : groupValue !== "";

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EEF1F4]">
          <h2 className="text-base font-bold text-ink">Add to group</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-ink3 hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4">
          <div className="mb-3">
            <label className="block text-[13px] text-ink mb-2 font-medium">Choose a group</label>
            <CustomDropdown
              value={groupValue}
              onChange={setGroupValue}
              options={options}
              placeholder="Select"
              searchable={existingGroups.length > 6}
              searchPlaceholder="Search groups"
            />
          </div>
          {isAddingNew && (
            <div className="mb-3">
              <label className="block text-[13px] text-ink mb-2 font-medium">New group name</label>
              <input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="Enter a name for your report group" className="w-full h-[42px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13.5px] bg-white focus:outline-none focus:border-teal-dark focus:ring-2 focus:ring-teal-dark/10" />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2.5 px-5 py-3 border-t border-[#EEF1F4] bg-[#F7F9FB]">
          <button type="button" onClick={onClose} className="h-[40px] px-5 border border-[#D8DEE6] rounded-[10px] text-[13.5px] font-semibold text-ink bg-white">Cancel</button>
          <button type="button" onClick={handleAdd} disabled={!canSave} className="h-[40px] px-5 border border-ink rounded-[10px] text-[13.5px] font-semibold text-white bg-ink disabled:opacity-40 disabled:cursor-not-allowed">Add to group</button>
        </div>
      </div>
    </div>,
    document.body
  );
}